import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { SSO_ENTRY_PATH, clearSsoConnection } from '../../../setup/auth0/ssoConnection'

const VALID_CONNECTION = /^[A-Za-z0-9_-]{1,128}$/

/**
 * Porta de entrada do SSO para quem não tem o link direto `/sso/<connection>`.
 *
 * Resolve o que foi digitado em um nome de connection e navega para esse link —
 * quem fala com a Auth0 continua sendo o redirect de `Routes.tsx`, para não
 * existirem dois caminhos de login.
 */
export function SsoEntryPage() {
  const intl = useIntl()
  const history = useHistory()

  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const resolveByDomain = async (domain: string): Promise<string | null> => {
    const apiUrl = process.env.REACT_APP_API_URL
    if (!apiUrl) return null

    try {
      const response = await fetch(
        `${apiUrl}/sso/connection-by-domain?domain=${encodeURIComponent(domain)}`
      )
      if (!response.ok) return null

      const data = await response.json()
      const connection = typeof data?.connection === 'string' ? data.connection : ''
      return VALID_CONNECTION.test(connection) ? connection : null
    } catch {
      return null
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const input = value.trim().toLowerCase()
    if (!input) {
      setError(intl.formatMessage({
        id: 'sso.entry.error.empty',
        defaultMessage: 'Informe seu e-mail corporativo ou o identificador da conexão.',
      }))
      return
    }

    setSubmitting(true)
    setError('')

    // Com "@" tratamos como e-mail e resolvemos pelo domínio; sem "@", o que foi
    // digitado já é o identificador da conexão.
    if (input.includes('@')) {
      const domain = input.split('@').pop() || ''
      const connection = domain ? await resolveByDomain(domain) : null

      if (!connection) {
        setSubmitting(false)
        setError(intl.formatMessage({
          id: 'sso.entry.error.unknown-domain',
          defaultMessage: 'Não reconhecemos esse domínio. Peça o link de acesso ao seu contato na Konfidency, ou informe o identificador da conexão.',
        }))
        return
      }

      history.push(`${SSO_ENTRY_PATH}/${connection}`)
      return
    }

    if (!VALID_CONNECTION.test(input)) {
      setSubmitting(false)
      setError(intl.formatMessage({
        id: 'sso.entry.error.invalid-identifier',
        defaultMessage: 'Identificador inválido. Use apenas letras, números, hífen e underscore.',
      }))
      return
    }

    history.push(`${SSO_ENTRY_PATH}/${input}`)
  }

  // Também serve de saída para quem ficou com uma conexão lembrada por engano.
  const handleUsePassword = () => {
    clearSsoConnection()
    window.location.assign('/')
  }

  return (
    <div className="d-flex flex-column flex-root">
      <div className="d-flex flex-column flex-column-fluid">
        <div className="d-flex flex-column flex-column-fluid text-center p-10 py-lg-20">
          <div className="pt-lg-10 mb-10 mx-auto w-100 max-w-500px">
            <h1 className="fw-bolder fs-2qx text-gray-800 mb-5">
              {intl.formatMessage({
                id: 'sso.entry.title',
                defaultMessage: 'Entrar com o SSO da sua empresa',
              })}
            </h1>

            <div className="fw-bold fs-5 text-muted mb-10">
              {intl.formatMessage({
                id: 'sso.entry.subtitle',
                defaultMessage: 'Informe seu e-mail corporativo. Se a sua empresa já tem SSO configurado, levamos você até ela.',
              })}
            </div>

            <form onSubmit={handleSubmit} className="text-start">
              <label className="form-label fw-bold" htmlFor="sso-entry-input">
                {intl.formatMessage({
                  id: 'sso.entry.field.label',
                  defaultMessage: 'E-mail corporativo ou identificador da conexão',
                })}
              </label>

              <input
                id="sso-entry-input"
                className="form-control form-control-lg mb-3"
                type="text"
                autoFocus
                autoComplete="username"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'sso.entry.field.placeholder',
                  defaultMessage: 'voce@suaempresa.com.br',
                })}
              />

              {error && (
                <div className="alert alert-danger py-3">{error}</div>
              )}

              <button
                type="submit"
                className="btn btn-lg btn-primary w-100 mb-5"
                disabled={submitting}
              >
                {submitting
                  ? intl.formatMessage({
                    id: 'sso.entry.submitting',
                    defaultMessage: 'Verificando…',
                  })
                  : intl.formatMessage({
                    id: 'sso.entry.submit',
                    defaultMessage: 'Continuar',
                  })}
              </button>
            </form>

            <button
              type="button"
              className="btn btn-link"
              onClick={handleUsePassword}
            >
              {intl.formatMessage({
                id: 'sso.entry.use-password',
                defaultMessage: 'Entrar com e-mail e senha',
              })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SsoEntryPage
